import { useState, useEffect } from "react";
import { listTenants, listLeads, markLeadsViewed } from "../lib/api";
import type { LeadView } from "../lib/api";

function fmtTime(ts: number) {
  if (!ts) return "—";
  const n = ts < 1e12 ? ts * 1000 : ts;
  return new Date(n).toLocaleString("zh-HK");
}

/** 複製格式：同事可以直接貼去 WhatsApp／CRM 跟進 */
function leadToText(l: LeadView): string {
  return [
    `【增值線索】#${l.id} · ${fmtTime(l.created_at)}`,
    `租戶：${l.tenant_id}`,
    `類型：${l.signal_type}`,
    `建議方案：${planForSignal(l.signal_type)}`,
    l.trigger_quote ? `觸發原話：${l.trigger_quote}` : null,
    "──────────────",
    l.content,
  ]
    .filter(Boolean)
    .join("\n");
}

// 方案映射（Captain 2026-09-07：two-plan＝Lite & Pro）
// L1 預設（轉真人通知＋跟進提醒）＝Lite 已內含；所有增值線索＝Pro 推介位
function planForSignal(signalType: string): string {
  return "Pro";
}

export default function LeadsPage() {
  const [tenants, setTenants] = useState<{ tenant_id: string }[]>([]);
  const [leads, setLeads] = useState<LeadView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("new");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await listTenants();
        if (data.success) setTenants(data.tenants || []);
      } catch {
        // 篩選器只是輔助，失敗不擋列表
      }
    })();
  }, []);

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantFilter, statusFilter]);

  async function loadLeads() {
    setLoading(true);
    setError("");
    try {
      const rows = await listLeads(
        tenantFilter || undefined,
        statusFilter === "all" ? undefined : statusFilter
      );
      setLeads(rows);
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }

  async function handleViewed(ids: number[]) {
    setMessage("");
    try {
      const data = await markLeadsViewed(ids);
      if (data.success) {
        setMessage(`✅ 已標記 ${ids.length} 條為已睇`);
        await loadLeads();
      } else {
        setMessage(`❌ 失敗：${data.error || "未知錯誤"}`);
      }
    } catch {
      setMessage("❌ 網路錯誤");
    }
  }

  async function handleCopy(l: LeadView) {
    try {
      await navigator.clipboard.writeText(leadToText(l));
      setCopiedId(l.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setMessage("❌ 複製失敗（瀏覽器權限？）");
    }
  }

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">🔥 增值線索</h1>
          <p className="text-sm text-slate-500 mt-1">
            阿Bot師傅訪談期間挖到嘅商機線索——未睇過會喺側欄長期閃光；複製全文可以直接貼去 WhatsApp／CRM 跟進
            <br />
            💡 方案對照（Lite &amp; Pro）：轉真人預設＋跟進提醒＝<b className="text-slate-300">Lite 已內含</b>；
            報價流程自動化／文件生成／落單流程自動化／舊客進度整合／多人協作＝<b className="text-amber-400">Pro 推介</b>
          </p>
        </div>
        {newCount > 0 && (
          <span className="px-3 py-1.5 rounded-full bg-rose-500/20 text-rose-400 text-sm font-bold animate-pulse border border-rose-500/40">
            {newCount} 條未睇
          </span>
        )}
      </div>

      <div className="flex gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
        >
          <option value="new">🔴 未睇（new）</option>
          <option value="viewed">✓ 已睇</option>
          <option value="all">全部</option>
        </select>
        <select
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200"
        >
          <option value="">全部租戶</option>
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.tenant_id}
            </option>
          ))}
        </select>
        <button
          onClick={loadLeads}
          className="px-3 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          ↻ 重新載入
        </button>
      </div>

      {message && (
        <div className="mb-4 text-sm text-slate-300 bg-slate-800/60 rounded-lg px-4 py-2">
          {message}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-sm">載入中…</div>
      ) : error ? (
        <div className="text-rose-400 text-sm">{error}</div>
      ) : leads.length === 0 ? (
        <div className="text-slate-500 text-sm bg-slate-900 rounded-xl p-8 text-center">
          冇線索——阿Bot師傅 v4 訪談期間偵測到老闆痛點（報價／人手流程／舊客進度）會自動存入呢度
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map((l) => (
            <div
              key={l.id}
              className={`rounded-xl border p-4 ${
                l.status === "new"
                  ? "border-rose-500/40 bg-slate-900 shadow-[0_0_18px_rgba(244,63,94,0.15)]"
                  : "border-slate-800 bg-slate-900/60"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {l.status === "new" && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-500/25 text-rose-400 text-xs font-bold animate-pulse">
                    ● NEW
                  </span>
                )}
                <span className="text-sm font-bold text-emerald-400">{l.tenant_id}</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-xs border border-amber-500/30">
                  {l.signal_type}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 text-xs border border-violet-500/30">
                  建議方案：{planForSignal(l.signal_type)}
                </span>
                <span className="text-xs text-slate-500 ml-auto">
                  {fmtTime(l.created_at)} · session {l.session_id.slice(0, 24)}…
                </span>
              </div>

              {l.trigger_quote && (
                <div className="text-sm text-slate-300 mb-2">
                  <span className="text-slate-500">觸發原話：</span>「{l.trigger_quote}」
                </div>
              )}

              <pre className="text-xs text-slate-400 whitespace-pre-wrap bg-slate-950/70 rounded-lg p-3 border border-slate-800 max-h-40 overflow-y-auto">
                {l.content}
              </pre>

              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleCopy(l)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600/20 text-emerald-400 border border-emerald-600/40 hover:bg-emerald-600/30"
                >
                  {copiedId === l.id ? "✓ 已複製" : "📋 複製全文"}
                </button>
                {l.status === "new" && (
                  <button
                    onClick={() => handleViewed([l.id])}
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    ✓ 已睇
                  </button>
                )}
              </div>
            </div>
          ))}

          {statusFilter === "new" && leads.length > 1 && (
            <button
              onClick={() => handleViewed(leads.map((x) => x.id))}
              className="w-full py-2 text-sm rounded-lg border border-slate-700 text-slate-400 hover:bg-slate-800"
            >
              全部標記已睇（{leads.length} 條）
            </button>
          )}
        </div>
      )}
    </div>
  );
}
